var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _HznRadioGroup_instances, _HznRadioGroup_emit, _HznRadioGroup_showGroupHeading_get, _HznRadioGroup_onKeydown, _HznRadioGroup_handleChange, _HznRadioGroup_handleInput, _HznRadioGroup_handleClick, _HznRadioGroup_renderRadioGroup;
// class definition
import { html, LitElement, nothing } from '@horizon/base';
import { property, query, queryAll, state } from '@horizon/base/decorators.js';
import { classMap } from '@horizon/base/directives.js';
import { HasSlotController } from '@horizon/common/controllers';
import { eventEmitter } from '@horizon/common/events';
import { FormControlMixin } from '@open-wc/form-control';
import { ScopedElementsMixin } from '@open-wc/scoped-elements';
import { submit } from '@open-wc/form-helpers';
import { HznInline } from '@horizon/inline';
import { HznStack } from '@horizon/stack';
import { HznText } from '@horizon/text';
import RadioGroupStyles from './radio-group.css.js';
import 'element-internals-polyfill';
/**
 *
 * @tag hzn-radio-group
 * @tagname hzn-radio-group
 * @summary Define a radio button group element in a form
 *
 * @fires {CustomEvent<{}>} input - Emitted every time a radio button in the group is selected
 * @fires {CustomEvent<{}>} change - Emitted when the radio group value is changed
 */
export class HznRadioGroup extends FormControlMixin(ScopedElementsMixin(LitElement)) {
    constructor() {
        super(...arguments);
        _HznRadioGroup_instances.add(this);
        /**
         * @private
         */
        _HznRadioGroup_emit.set(this, eventEmitter(this));
        // automated requestUpdate() on slotchange event
        this.hasSlotController = new HasSlotController(this, '[default]');
        /**
         * @private
         */
        this.radios = [];
        /**
         * Sets the input value
         */
        this.value = '';
        /**
         * Set whether or not the input is required to have a value
         */
        this.required = false;
        /**
         * Set the input to be disabled
         */
        this.disabled = false;
        /**
         * Set the help text for the input
         */
        this.helperText = '';
        /**
         * Hides the help text for the radio-group
         */
        this.hideHelperText = false;
        /**
         * Set the fieldset legend text content for the radio group
         */
        this.legend = '';
        /**
         * Set the legend to be visually hidden (still present for screen readers)
         */
        this.hideLegend = false;
        /**
         * Set the radio group to display horizontally. Best used for less than 3 options.
         */
        this.horizontal = false;
    }
    static get scopedElements() {
        return {
            'hzn-inline': HznInline,
            'hzn-stack': HznStack,
            'hzn-text': HznText,
        };
    }
    // set a reactive property so that error messages will show when applicable
    /**
     * @private
     */
    validationMessageCallback(message) {
        this.internalErrorMessage = message;
    }
    /**
     * @private
     */
    resetFormControl() {
        this.radios.map((radio) => (radio.checked = false));
        this.value = '';
    }
    /**
     * @private
     */
    updated(changed) {
        if (changed.has('value')) {
            this.setValue(this.value);
        }
    }
    /**
     * Sets focus on the radio group
     */
    focus(options) {
        this.validationTarget.focus(options);
    }
    /**
     * Removes focus from the radio group
     */
    blur() {
        this.validationTarget.blur();
    }
    connectedCallback() {
        super.connectedCallback();
        this.shadowRoot.addEventListener('slotchange', () => {
            this.radios = Array.from(this.querySelectorAll('hzn-radio'));
            const checked = this.radios.filter((el) => el.checked === true);
            if (checked.length > 0) {
                this.value = checked[0].value;
                // @ts-ignore
                this.internals.setFormValue(this.value);
            }
        });
        // hide default browser invalid popup
        this.addEventListener('invalid', (event) => event.preventDefault());
    }
    render() {
        const lastChecked = [...this.radios].reverse().find((radioElement) => radioElement.checked === true);
        return html `
      <fieldset aria-describedby="group-helper-text">
        <hzn-stack space="large">
          <hzn-stack space="small" class="${'group-heading' + (__classPrivateFieldGet(this, _HznRadioGroup_instances, "a", _HznRadioGroup_showGroupHeading_get) ? ' visually-hidden' : '')}">
            <legend class="${this.hideLegend ? 'visually-hidden' : ''}">
              <hzn-text weight="bold">${this.legend || this.name}</hzn-text>
            </legend>
            ${this.helperText
            ? html `<hzn-text
                  class="${'group-helper-text' + (this.hideHelperText ? ' visually-hidden' : '')}"
                  id="group-helper-text"
                  variant="caption"
                  tone="${this.disabled ? 'disabled' : 'subdued'}"
                >
                  ${this.helperText}
                </hzn-text>`
            : nothing}
            ${this.internalErrorMessage
            ? html `<hzn-text class="error-message" variant="caption" tone="critical">
                  ${this.internalErrorMessage}
                </hzn-text>`
            : nothing}
          </hzn-stack>

          ${!this.horizontal
            ? html `<hzn-stack space="medium" class="radio-group"> ${__classPrivateFieldGet(this, _HznRadioGroup_instances, "m", _HznRadioGroup_renderRadioGroup).call(this, lastChecked)} </hzn-stack>`
            : html `<hzn-inline space="large" class="radio-group" align-y="start">
                ${__classPrivateFieldGet(this, _HznRadioGroup_instances, "m", _HznRadioGroup_renderRadioGroup).call(this, lastChecked)}
              </hzn-inline>`}
        </hzn-stack>
        <slot hidden></slot>
      </fieldset>
    `;
    }
}
_HznRadioGroup_emit = new WeakMap(), _HznRadioGroup_instances = new WeakSet(), _HznRadioGroup_showGroupHeading_get = function _HznRadioGroup_showGroupHeading_get() {
    if (this.hideLegend && !this.internalErrorMessage) {
        if (!this.helperText) {
            return true;
        }
        if (this.hideHelperText) {
            return true;
        }
    }
    return false;
}, _HznRadioGroup_onKeydown = function _HznRadioGroup_onKeydown(event) {
    const hasModifier = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
    // Pressing enter when focused on an input should submit the form like a native input
    if (event.code === 'Enter' && !hasModifier) {
        if (this.form) {
            submit(this.form);
        }
    }
}, _HznRadioGroup_handleChange = function _HznRadioGroup_handleChange(event) {
    event.stopImmediatePropagation();
    event.preventDefault();
    this.radios.forEach((radio, index) => {
        const targetIndex = Array.from(this.innerRadioInputs).findIndex((el) => el === event.target);
        radio.checked = index === targetIndex;
    });
    __classPrivateFieldGet(this, _HznRadioGroup_emit, "f").call(this, { type: 'change' });
    this.requestUpdate();
}, _HznRadioGroup_handleInput = function _HznRadioGroup_handleInput(event) {
    event.stopImmediatePropagation();
    event.preventDefault();
    this.value = event.target.value;
    __classPrivateFieldGet(this, _HznRadioGroup_emit, "f").call(this, { type: 'input' });
}, _HznRadioGroup_handleClick = function _HznRadioGroup_handleClick(event) {
    event.stopImmediatePropagation();
    event.preventDefault();
    const innerInput = event.target.previousElementSibling;
    innerInput.click();
}, _HznRadioGroup_renderRadioGroup = function _HznRadioGroup_renderRadioGroup(lastChecked) {
    return html `${this.radios.map((radioElement, index) => {
        const radioControlClasses = {
            'radio-control': true,
            checked: Boolean(radioElement.checked && lastChecked === radioElement),
            disabled: Boolean(this.disabled),
            error: this.internalErrorMessage,
        };
        const focusHoverHandler = (event) => {
            if (!this.disabled) {
                const textEl = event.currentTarget.querySelector('.label');
                const radioEl = event.currentTarget.querySelector('input[type="radio"]');
                if (!radioEl.checked) {
                    textEl.weight = textEl.weight === 'bold' ? 'base' : 'bold';
                }
            }
        };
        return html ` <hzn-inline
        space="base"
        align-y="start"
        class="radio-element ${this.horizontal ? 'horizontal' : ''}"
        @mouseover="${focusHoverHandler}"
        @mouseout="${focusHoverHandler}"
        @focusin="${focusHoverHandler}"
        @focusout="${focusHoverHandler}"
      >
        <div class="${classMap(radioControlClasses)}">
          <input
            class="visually-hidden"
            type="radio"
            name=${this.name}
            .value="${radioElement.value}"
            id="radio-${index}"
            .checked=${radioElement.checked && lastChecked === radioElement}
            .disabled=${this.disabled}
            ?required="${this.required}"
            aria-checked=${radioElement.checked && lastChecked === radioElement ? 'true' : 'false'}
            aria-describedby="helper-text-${index}"
            @change=${__classPrivateFieldGet(this, _HznRadioGroup_instances, "m", _HznRadioGroup_handleChange)}
            @input=${__classPrivateFieldGet(this, _HznRadioGroup_instances, "m", _HznRadioGroup_handleInput)}
            @keydown="${__classPrivateFieldGet(this, _HznRadioGroup_instances, "m", _HznRadioGroup_onKeydown)}"
          />
          <span aria-hidden="true" @click="${__classPrivateFieldGet(this, _HznRadioGroup_instances, "m", _HznRadioGroup_handleClick)}"></span>
        </div>
        <!-- xsmall space here, because the label is styled to equal the height of the radio control for alignment -->
        <hzn-stack space="xsmall">
          <label for="radio-${index}" class="radio-label">
            <hzn-text
              class="label"
              tone="${this.disabled ? 'disabled' : ''}"
              weight="${radioElement.checked && lastChecked === radioElement ? 'bold' : 'base'}"
            >
              ${radioElement.textContent || `Radio button ${index + 1}`}
            </hzn-text>
          </label>
          ${radioElement.helperText
            ? html ` <hzn-text
                class="${'helper-text' + (this.hideHelperText ? ' visually-hidden' : '')}"
                id="helper-text-${index}"
                variant="caption"
                tone="${this.disabled ? 'disabled' : 'subdued'}"
              >
                ${radioElement.helperText}
              </hzn-text>`
            : nothing}
        </hzn-stack>
      </hzn-inline>`;
    })}`;
};
HznRadioGroup.styles = [RadioGroupStyles];
HznRadioGroup.formControlValidators = [
    {
        key: 'valueMissing',
        message(instance) {
            if (instance.errorMessage) {
                return instance.errorMessage;
            }
            return `${instance.displayName ? instance.displayName : 'This field'} is required`;
        },
        isValid(instance, value) {
            if ((instance.hasAttribute('required') || instance.required) && !value) {
                return false;
            }
            return true;
        },
    },
];
__decorate([
    state()
], HznRadioGroup.prototype, "radios", void 0);
__decorate([
    state()
], HznRadioGroup.prototype, "internalErrorMessage", void 0);
__decorate([
    query('input[type="radio"]')
], HznRadioGroup.prototype, "validationTarget", void 0);
__decorate([
    queryAll('input[type="radio"]')
], HznRadioGroup.prototype, "innerRadioInputs", void 0);
__decorate([
    property()
], HznRadioGroup.prototype, "name", void 0);
__decorate([
    property({ type: String, attribute: 'display-name' })
], HznRadioGroup.prototype, "displayName", void 0);
__decorate([
    property({ type: String })
], HznRadioGroup.prototype, "value", void 0);
__decorate([
    property({ type: Boolean })
], HznRadioGroup.prototype, "required", void 0);
__decorate([
    property({ type: Boolean })
], HznRadioGroup.prototype, "disabled", void 0);
__decorate([
    property({ type: String, attribute: 'helper-text' })
], HznRadioGroup.prototype, "helperText", void 0);
__decorate([
    property({ type: Boolean, attribute: 'hide-helper-text' })
], HznRadioGroup.prototype, "hideHelperText", void 0);
__decorate([
    property({ type: String, attribute: 'error-message', reflect: true })
], HznRadioGroup.prototype, "errorMessage", void 0);
__decorate([
    property({ type: String })
], HznRadioGroup.prototype, "legend", void 0);
__decorate([
    property({ type: Boolean, attribute: 'hide-legend' })
], HznRadioGroup.prototype, "hideLegend", void 0);
__decorate([
    property({ type: Boolean })
], HznRadioGroup.prototype, "horizontal", void 0);
//# sourceMappingURL=radio-group.js.map